<script lang="ts">
	import { clickOutsidePortal } from '../events/events'
	import { type Snippet } from 'svelte'
	import Portal from 'svelte-portal'

	interface Props {
		placeholderElement: HTMLDivElement
		maxWidth?: number
		content: Snippet<[Snippet]>
		onClickOutsidePortal: () => void
		children?: Snippet
	}

	let {
		placeholderElement,
		onClickOutsidePortal,
		content,
		children,
		maxWidth = 240,
	}: Props = $props()

	const dropdownOffset = 4

	let dropdownContentClientHeight: number = $state(null)
	let pageInnerHeight: number = $state(null)
	let pageInnerWidth: number = $state(null)

	function setDropdownPosition(
		dropdownContentHeight: number,
		placeholderPosition: DOMRect,
		pageInnerHeight: number,
		pageInnerWidth: number,
	) {
		let dropdownContentTop = placeholderPosition.bottom + dropdownOffset

		if (dropdownContentTop + dropdownContentHeight > pageInnerHeight) {
			dropdownContentTop = placeholderPosition.top - dropdownContentHeight - dropdownOffset

			if (dropdownContentTop < dropdownOffset) {
				dropdownContentTop = placeholderPosition.bottom + dropdownOffset
			}
		}

		const maxAvailableWidth = pageInnerWidth - 2 * dropdownOffset
		const preferredWidth = Math.max(placeholderElement.clientWidth, maxWidth)
		const finalWidth = Math.min(preferredWidth, maxAvailableWidth)

		let dropdownLeft = placeholderPosition.right - finalWidth

		if (dropdownLeft < dropdownOffset) {
			dropdownLeft = placeholderPosition.left
			if (dropdownLeft + finalWidth > pageInnerWidth - dropdownOffset) {
				dropdownLeft = pageInnerWidth - finalWidth - dropdownOffset
			}
		}

		if (dropdownLeft < dropdownOffset) {
			dropdownLeft = dropdownOffset
		}

		return {
			top: dropdownContentTop,
			left: dropdownLeft,
			width: finalWidth,
		}
	}

	let placeholderPosition: DOMRect = $derived(placeholderElement.getBoundingClientRect())
	const dropdownContentStyle = $derived(
		setDropdownPosition(
			dropdownContentClientHeight,
			placeholderPosition,
			pageInnerHeight,
			pageInnerWidth,
		),
	)

	function handleUpdatePlaceholderPosition() {
		placeholderPosition = placeholderElement.getBoundingClientRect()
	}
</script>

<Portal target="#root">
	<div
		style:top="{dropdownContentStyle.top}px"
		style:left="{dropdownContentStyle.left}px"
		style:width="{dropdownContentStyle.width}px"
		class="font-body fixed z-30 text-base"
		data-prevent-click-outside
		data-test="select-dropdown-content"
		onclickOutsidePortal={onClickOutsidePortal}
		bind:clientHeight={dropdownContentClientHeight}
		use:clickOutsidePortal
	>
		{@render content(children)}
	</div>
</Portal>

<svelte:window
	onresize={handleUpdatePlaceholderPosition}
	onscrollcapture={handleUpdatePlaceholderPosition}
	bind:innerHeight={pageInnerHeight}
	bind:innerWidth={pageInnerWidth}
/>
